// ─────────────────────────────────────────────────────────────
// MaintenanceScreen — full-viewport screen shown to non-admin
// users while site-wide maintenance is on.
//
// Owns no async state of its own. The screen polls
// `GET /api/system/maintenance` every 30 seconds and auto-clears
// itself when `enabled` flips to `false` (handled at the
// `useMaintenance` hook layer; this component is purely visual).
//
// If `onAdminLogin` is provided we render a small "Admin? Sign in
// here" link that the parent uses to swap the screen for the
// login form so admins can still authenticate.
// ─────────────────────────────────────────────────────────────

import { Box, Button, Paper, Typography } from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { formatRelativeTime } from "../utils/formatRelativeTime";

export interface MaintenanceScreenProps {
  message: string | null;
  updatedAt: string | null;
  /** When set, renders the "Admin? Sign in here" affordance. */
  onAdminLogin?: () => void;
}

const DEFAULT_MESSAGE =
  "Maps Discover Studio is being updated. We'll be back shortly.";

export function MaintenanceScreen({
  message,
  updatedAt,
  onAdminLogin,
}: MaintenanceScreenProps) {
  const displayMessage = message ?? DEFAULT_MESSAGE;
  const updatedRelative = updatedAt ? formatRelativeTime(updatedAt) : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: 4,
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 2,
        }}
      >
        <BuildOutlinedIcon
          sx={{
            fontSize: 56,
            color: "warning.main",
          }}
          aria-hidden="true"
        />

        <Typography variant="h1" component="h1" sx={{ fontWeight: 500 }}>
          Back shortly
        </Typography>

        <Typography
          variant="body1"
          color="text.primary"
          sx={{
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
            // Long pasted-in admin messages should wrap, not overflow.
            wordBreak: "break-word",
          }}
        >
          {displayMessage}
        </Typography>

        {updatedRelative && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Last updated: {updatedRelative}
          </Typography>
        )}

        {onAdminLogin && (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={onAdminLogin}
              sx={{ textTransform: "none" }}
            >
              Admin? Sign in here
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
